import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { DobleAutenticacionService } from 'src/app/services/doble-autenticacion.service';
import { MicroempresarioService } from '../services/microempresario.service';
import { ParametrosService } from '../../services/parametros.service';
import { UserService } from '../../services/user.service';
import { SweetAlertService } from '../../services/sweet-alert.service';
import { BancoldexService } from 'src/app/comun/services/bancoldex.service';
import { ReCaptchaV3Service } from 'ng-recaptcha-2';
import { consumeLoginPrefillFromLanding } from 'src/app/core/login-prefill.storage';
import { setAuthFlowOrigen } from 'src/app/core/auth-flow-origen.storage';
import {
  getDocumentPatternMessage,
  numeroDocumentoPorTipoDocValidator,
} from 'src/app/shared/utils/form-validators.util';
import { PASSWORD_COMPLEXITY_PATTERN } from 'src/app/shared/utils/validation.constants';
import { AnalyticsTagService } from 'src/app/services/analytics-tag.service';
import { UtmTrackingService } from 'src/app/services/utm-tracking.service';

const mensajeBloqueado = 'PAR-110';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false,
})
export class LoginComponent implements OnInit {
  documentos: any[] = [];
  loginForm: UntypedFormGroup;
  isVisible = false;
  validarContrasena = PASSWORD_COMPLEXITY_PATTERN;
  mensajeBloqueo = '';
  captchaToken: string | null = null;
  captchaFailCount = 0;
  isBlockedCaptcha = false;
  private captchaSubmitting = false;

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly _microempresario: MicroempresarioService,
    private readonly _parametro: ParametrosService,
    private readonly _user: UserService,
    private readonly _dobleAutenticacion: DobleAutenticacionService,
    private readonly route: Router,
    private readonly _sweetAlert: SweetAlertService,
    private readonly _bancoldexService: BancoldexService,
    private readonly recaptchaV3Service: ReCaptchaV3Service,
    private readonly analyticsTag: AnalyticsTagService,
    private readonly utmTracking: UtmTrackingService
  ) {
    this.loginForm = this.formBuilder.group({
      tipoDocumento: ['', Validators.required],
      numeroDocumento: [
        '',
        [
          Validators.required,
          Validators.maxLength(15),
          numeroDocumentoPorTipoDocValidator(() => this.documentos, 'tipoDocumento'),
        ],
      ],
      password: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          Validators.pattern(this.validarContrasena),
        ],
      ],
    });
  }

  get mensajeErrorNumeroDocumento(): string {
    return getDocumentPatternMessage(this.loginForm.get('numeroDocumento'));
  }

  ngOnInit(): void {
    setAuthFlowOrigen('micro');
    const blockTime = localStorage.getItem('captchaBlock');
    if (blockTime) {
      const currentTime = Date.now();
      const unblockTime = Number.parseInt(blockTime, 10);
      if (currentTime < unblockTime) {
        this.isBlockedCaptcha = true;
        const remainingTime = unblockTime - currentTime;
        setTimeout(() => {
          this.isBlockedCaptcha = false;
          this.captchaFailCount = 0;
          this.clearCaptchaToken();
          localStorage.removeItem('captchaBlock');
        }, remainingTime);
      } else {
        this.isBlockedCaptcha = false;
        localStorage.removeItem('captchaBlock');
      }
    }
    this.getMensajeBloqueo();
    this.getDocumentos();
    this.applyLandingPrefill();
  }

  /** Precarga tipo + número solo si el CTA vino del landing (one-shot). */
  private applyLandingPrefill(): void {
    const prefill = consumeLoginPrefillFromLanding();
    if (!prefill) {
      return;
    }
    this.loginForm.patchValue({
      tipoDocumento: String(prefill.tipoDocumento),
      numeroDocumento: prefill.numeroDocumento,
    });
  }

  getDocumentos() {
    this._parametro.getTipoDocumentos().subscribe((data: any) => {
      this.documentos = data.filter(
        (documento: any) =>
          documento.personaJuridica != 1 &&
          documento.idTipoDocIdentidad != 4 &&
          documento.idTipoDocIdentidad != 6
      );
      this.loginForm.get('numeroDocumento')?.updateValueAndValidity();
    });
  }

  login() {
    if (this.isBlockedCaptcha) {
      this._sweetAlert.errorModal(
        'Demasiados intentos',
        true,
        'Tu acceso ha sido temporalmente bloqueado, por seguridad, debido a múltiples intentos incorrectos. Inténtalo nuevamente en 2 minutos.'
      );
      return;
    }
    const loginBody = {
      tipoDocumento: +this.loginForm.get('tipoDocumento')!.value,
      numeroDocumento: this.loginForm.get('numeroDocumento')!.value,
      password: this.loginForm.get('password')!.value,
      captchaToken: this.captchaToken,
    };
    this._microempresario.login(loginBody).then((data: any) => {
      this.captchaFailCount++;
      this.handleLoginResponse(data);
      if (this.captchaFailCount > 3) {
        this.blockCaptcha();
      } else {
        this.clearCaptchaToken();
      }
    });
  }

  private handleLoginResponse(data: any): void {
    if (data.exitoso === 'true') {
      this.handleLoginSuccess(data);
      return;
    }
    if (data.exitoso === 'false' && data.codigoError) {
      this._sweetAlert.errorModal(
        '¡Error!',
        true,
        data.error ? data.error : data.mensajeError
      );
      return;
    }
    if (data.exitoso === false && data.enabled === false) {
      this._sweetAlert.errorModal('Error!', true, data.error);
      return;
    }
    if (data.exitoso === false && data.bloked) {
      this.handleBlockedUser(data);
      return;
    }
    this.handleUnregisteredUser(data);
  }

  private handleLoginSuccess(data: any): void {
    if (data.segundoFactorAut) {
      this._parametro.sendOtp(data.correoElectronico).subscribe((response: any) => {
        if (response.exitoso === 'true') {
          this.captchaFailCount = 0;
          this._dobleAutenticacion.saveParametros({
            email: data.correoElectronico,
            token: response.token,
            path: 'microempresario',
            data: {
              ...data,
              numeroDocumento:
                data?.numeroDocumento ||
                this.loginForm.get('numeroDocumento')?.value,
            },
            numeroDocumento: this.loginForm.get('numeroDocumento')?.value,
          });
          this.route.navigateByUrl('/doble-autenticacion');
        } else {
          this._sweetAlert.errorModal(
            '¡Error!',
            true,
            response.error ? response.error : response.mensajeError
          );
        }
      });
      return;
    }
    this.captchaFailCount = 0;
    this._user.saveUser({
      idSolicitud: '',
      ...data,
      numeroDocumento:
        data?.numeroDocumento || this.loginForm.get('numeroDocumento')?.value,
    });
    this.utmTracking.sincronizarTrasIdentificar({
      numeroDocumento: this.loginForm.get('numeroDocumento')?.value,
      correoElectronico: data?.correoElectronico,
      razonSocial: data?.nombre || data?.razonSocial,
    });
    this.route.navigateByUrl('microempresario/home');
  }

  private handleBlockedUser(data: any): void {
    this._sweetAlert
      .errorModal('Error!', true, data.error, 'Desbloquear Aquí')
      .then((result) => {
        if (result.isConfirmed) {
          this.route.navigateByUrl('/desbloquear');
          localStorage.removeItem('captchaBlock');
        }
      });
  }

  private handleUnregisteredUser(data: any): void {
    const rawLogin = sessionStorage.getItem('login');
    const bloquear = rawLogin ? JSON.parse(rawLogin) : null;
    const tipoDocumento = this.loginForm.get('tipoDocumento')!.value;
    const numeroDocumento = this.loginForm.get('numeroDocumento')!.value;
    const isChange =
      tipoDocumento == bloquear?.tipoDocumento &&
      numeroDocumento == bloquear?.numeroDocumento;
    const isBloked = bloquear?.contador > 2 ? isChange : false;

    let errorMessage = data.error;
    if (data.mensajeError) {
      errorMessage = data.mensajeError;
    }
    if (isBloked) {
      errorMessage = this.mensajeBloqueo;
    }
    const confirmButton = isBloked ? 'Desbloquear Aquí' : 'Aceptar';

    this._sweetAlert
      .errorModal('¡Error!', true, errorMessage, confirmButton)
      .then((result) => {
        if (result.isConfirmed && isBloked) {
          this.route.navigateByUrl('/desbloquear');
          sessionStorage.removeItem('login');
          localStorage.removeItem('captchaBlock');
        }
      });

    let contador = 1;
    if (bloquear?.contador && isChange) {
      contador = bloquear.contador + 1;
    }
    const login = {
      tipoDocumento: +tipoDocumento,
      numeroDocumento,
      password: this.loginForm.get('password')!.value,
      contador,
    };
    sessionStorage.setItem('login', JSON.stringify(login));
  }

  validateField(key: string) {
    const control = this.loginForm.get(key);
    return (control?.dirty || control?.touched) && control?.invalid;
  }

  showPassword() {
    this.isVisible = !this.isVisible;
  }

  trackLinkCta(ctaLabel: string, ctaElementId: string): void {
    this.analyticsTag.trackCtaClick({
      ctaLabel,
      ctaSection: 'login-microempresario',
      ctaComponent: 'login-micro',
      ctaElementId,
      hu: '8337',
    });
  }

  validarSelect() {
    this.loginForm.get('numeroDocumento')!.setValue('');
    this.loginForm.get('numeroDocumento')?.updateValueAndValidity();
  }

  validateForm() {
    if (!this.loginForm.valid) {
      this.loginForm.markAllAsTouched();
      return;
    }
    if (this.captchaSubmitting) {
      return;
    }
    this.analyticsTag.trackCtaClick({
      ctaLabel: 'Inicia sesión',
      ctaSection: 'login-microempresario',
      ctaComponent: 'login-micro',
      ctaElementId: 'btn-login-micro-iniciar',
      hu: '8337',
    });
    this.captchaSubmitting = true;
    this.recaptchaV3Service.execute('login_microempresario').subscribe({
      next: (token) => {
        this.captchaToken = token;
        this.captchaSubmitting = false;
        this.login();
      },
      error: () => {
        this.captchaSubmitting = false;
        this._sweetAlert.errorModal(
          '¡Error!',
          true,
          'No se pudo validar el captcha. Inténtalo de nuevo.'
        );
      },
    });
  }

  getMensajeBloqueo() {
    const json = {
      idParametro: mensajeBloqueado,
      nombre: null,
      isParFuncional: true,
      pagina: 1,
    };
    this._bancoldexService.getFilterParametro(json).subscribe(
      (response: any) => {
        const respuesta = response.data;
        this.mensajeBloqueo = respuesta[0].valorParametro;
      },
      (error) => {
        this._sweetAlert.errorModal('Error!', true, error.error);
      }
    );
  }

  clearCaptchaToken() {
    this.captchaToken = null;
  }

  blockCaptcha() {
    const block = Date.now() + 2 * 60 * 1000;
    localStorage.setItem('captchaBlock', block.toString());
    this.isBlockedCaptcha = true;
    setTimeout(() => {
      this.isBlockedCaptcha = false;
      this.captchaFailCount = 0;
      this.clearCaptchaToken();
      localStorage.removeItem('captchaBlock');
    }, 2 * 60 * 1000);
  }
}
